import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import { erpIntegrationService } from '../../services/erpIntegrationService';
import ConnectionStatusDashboard from './components/ConnectionStatusDashboard';
import ERPConnectorMatrix from './components/ERPConnectorMatrix';
import FieldMappingInterface from './components/FieldMappingInterface';
import SyncSchedulingPanel from './components/SyncSchedulingPanel';
import AuditLogPanel from './components/AuditLogPanel';

const ERPIntegrationHub = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [activeTab, setActiveTab] = useState('connections');
  const [syncStats, setSyncStats] = useState(null);

  useEffect(() => {
    loadConnections();
  }, []);

  useEffect(() => {
    if (selectedConnection) {
      loadSyncStatistics(selectedConnection?.id);
    }
  }, [selectedConnection]);

  const loadConnections = async () => {
    try {
      setLoading(true);
      const data = await erpIntegrationService?.getAllConnections();
      setConnections(data);
      if (data?.length > 0 && !selectedConnection) {
        setSelectedConnection(data?.[0]);
      }
    } catch (error) {
      console.error('Error loading ERP connections:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSyncStatistics = async (connectionId) => {
    try {
      const stats = await erpIntegrationService?.getSyncStatistics(connectionId);
      setSyncStats(stats);
    } catch (error) {
      console.error('Error loading sync statistics:', error);
    }
  };

  const handleConnectionSelect = (connection) => {
    setSelectedConnection(connection);
    setActiveTab('connections');
  };

  const handleTestConnection = async (connectionId) => {
    try {
      await erpIntegrationService?.testConnection(connectionId);
      await loadConnections();
    } catch (error) {
      console.error('Error testing connection:', error);
    }
  };

  const tabs = [
    { id: 'connections', label: 'System Connections', icon: 'Link' },
    { id: 'mapping', label: 'Field Mapping', icon: 'GitBranch' },
    { id: 'scheduling', label: 'Sync Scheduling', icon: 'Clock' },
    { id: 'audit', label: 'Audit Trail', icon: 'FileText' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-4 py-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-3xl font-bold text-foreground">ERP Integration Hub</h1>
              <p className="text-muted-foreground mt-1">
                Seamless connectivity with PNG ERP systems for automated data synchronization
              </p>
            </div>
            <button
              onClick={() => navigate('/main-dashboard-hub')}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
            >
              <Icon name="ArrowLeft" size={16} />
              Back to Dashboard
            </button>
          </div>
        </div>

        {/* Connection Status Dashboard */}
        <div className="mb-6">
          <ConnectionStatusDashboard
            connections={connections}
            syncStats={syncStats}
            selectedConnection={selectedConnection}
            onConnectionSelect={handleConnectionSelect}
            onTestConnection={handleTestConnection}
          />
        </div>

        {/* Main Content Area */}
        <div className="bg-card border border-border rounded-lg">
          {/* Tabs */}
          <div className="border-b border-border">
            <div className="flex items-center gap-1 p-1">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-all ${
                    activeTab === tab?.id
                      ? 'bg-primary text-white' :'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span className="font-medium">{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading ERP connections...</p>
                </div>
              </div>
            ) : (
              <>
                {activeTab === 'connections' && (
                  <ERPConnectorMatrix
                    connections={connections}
                    onConnectionCreated={loadConnections}
                    onConnectionUpdated={loadConnections}
                  />
                )}

                {activeTab === 'mapping' && selectedConnection && (
                  <FieldMappingInterface
                    connection={selectedConnection}
                  />
                )}

                {activeTab === 'scheduling' && selectedConnection && (
                  <SyncSchedulingPanel
                    connection={selectedConnection}
                  />
                )}

                {activeTab === 'audit' && selectedConnection && (
                  <AuditLogPanel
                    connection={selectedConnection}
                  />
                )}

                {activeTab !== 'connections' && !selectedConnection && (
                  <div className="text-center py-12">
                    <Icon name="AlertCircle" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Please select an ERP connection first</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ERPIntegrationHub;
